import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Product {
  id: number;
  name: string;
  price: number;
  stock_quantity: number;
  images?: {
    signedUrl: string;
    isPrimary: boolean;
  }[];
}

const AdminProducts: React.FC = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BASE_URL;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  const [deleteId, setDeleteId] = useState<number | null>(null);

  /* ================== HELPERS ================== */
  const getStockStatus = (qty: number) => {
    if (qty === 0) return "out";
    if (qty < 5) return "low";
    return "in";
  };

  /* ================== FETCH PRODUCTS ================== */
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/products?page=${currentPage}&limit=${itemsPerPage}&search=${searchTerm}`,
          { credentials: "include" }
        );
        const data = await res.json();

        setProducts(Array.isArray(data.data) ? data.data : []);
        setTotalPages(data.totalPages || 1);
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API_URL, currentPage, searchTerm]);

  /* ================== DELETE ================== */
  const deleteProduct = async () => {
    if (!deleteId) return;

    try {
      await fetch(`${API_URL}/products/${deleteId}`, {
        method: "DELETE",
        credentials: "include",
      });

      setProducts((prev) => prev.filter((p) => p.id !== deleteId));
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading products...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* ================= HEADER ================= */}
      <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Products</h2>
        <button
          onClick={() => navigate("/addproduct")}
          className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
        >
          + Add Product
        </button>
      </div>

      {/* ================= SEARCH ================= */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full sm:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring"
        />
      </div>

      {/* ================= TABLE (DESKTOP) ================= */}
      <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border-b px-4 py-3">Image</th>
              <th className="border-b px-4 py-3">Name</th>
              <th className="border-b px-4 py-3">Price</th>
              <th className="border-b px-4 py-3">Stock</th>
              <th className="border-b px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {products.length ? (
              products.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="border-b px-4 py-3">
                    {p.images?.[0] && (
                      <img
                        src={p.images[0].signedUrl}
                        alt={p.name}
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                    )}
                  </td>

                  <td className="border-b px-4 py-3 font-medium">
                    {p.name}
                  </td>

                  <td className="border-b px-4 py-3">
                    UGX {p.price.toLocaleString()}
                  </td>

                  <td className="border-b px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        getStockStatus(p.stock_quantity) === "out"
                          ? "bg-red-100 text-red-700"
                          : getStockStatus(p.stock_quantity) === "low"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {getStockStatus(p.stock_quantity) === "out"
                        ? "Out of stock"
                        : getStockStatus(p.stock_quantity) === "low"
                        ? "Low stock"
                        : "In stock"}
                    </span>
                  </td>

                  <td className="border-b px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => navigate(`/products/edit/${p.id}`)}
                        className="rounded-md border border-indigo-600 px-3 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteId(p.id)}
                        className="rounded-md border border-red-600 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No products found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE CARDS ================= */}
      <div className="sm:hidden space-y-3">
        {products.map((p) => (
          <div
            key={p.id}
            className="rounded-lg border bg-white p-4 shadow-sm"
          >
            <div className="flex gap-4">
              {p.images?.[0] && (
                <img
                  src={p.images[0].signedUrl}
                  className="h-20 w-20 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <h4 className="font-medium">{p.name}</h4>
                <p className="text-sm text-gray-600">
                  UGX {p.price.toLocaleString()}
                </p>

                <div className="mt-2 flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Stock: {p.stock_quantity}
                  </span>
                  <button
                    onClick={() => navigate(`/products/edit/${p.id}`)}
                    className="text-indigo-600 text-sm"
                  >
                    Edit →
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ================= PAGINATION ================= */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-1">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`rounded-md px-3 py-1.5 text-sm border ${
                currentPage === i + 1
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "border-gray-300 hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* ================= DELETE MODAL ================= */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold">Delete product?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This action cannot be undone.
            </p>

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={deleteProduct}
                className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
